import { Moment } from 'moment';

// The backend refuses anything further out (BL-TASK-1444). Elapsed time, as
// the server measures it: across a DST change 90 calendar days is an hour
// more or less than this, and the server would refuse the extra hour.
export const MAX_SNOOZE_DAYS = 90;
export const MAX_SNOOZE_MS = MAX_SNOOZE_DAYS * 24 * 60 * 60 * 1000;

export interface SnoozePreset {
  key: 'later' | 'tomorrow' | 'monday';
  label: string;
  until: Moment;
}

const at9am = (day: Moment) =>
  day.clone().startOf('day').set({ hour: 9, minute: 0, second: 0 });

/**
 * The menu's presets, in the zone `now` carries — the browser's in the app.
 * Days are stepped on the calendar, not by 24 hours, so "tomorrow 9 AM" is
 * still 9 AM on the far side of a DST change.
 */
export const snoozePresets = (now: Moment): SnoozePreset[] => {
  const presets: SnoozePreset[] = [];
  // Two to three hours out, on the hour. Past ~9 PM that is tomorrow, which
  // the next preset already covers better.
  const later = now.clone().add(3, 'hours').startOf('hour');
  if (later.isSame(now, 'day')) {
    presets.push({ key: 'later', label: 'Later today', until: later });
  }
  const tomorrow = at9am(now.clone().add(1, 'day'));
  presets.push({ key: 'tomorrow', label: 'Tomorrow', until: tomorrow });
  // Always a Monday still to come: on a Monday it is next week's.
  const monday = at9am(now.clone().startOf('isoWeek').add(1, 'week'));
  // On a Sunday it is the same moment as "Tomorrow".
  if (!monday.isSame(tomorrow)) {
    presets.push({ key: 'monday', label: 'Monday', until: monday });
  }
  return presets;
};

/** The client-side twin of the backend's check, so most mistakes never leave the dialog. */
export const snoozeTimeError = (
  until: Moment | null,
  now: Moment,
): string | null => {
  if (!until || !until.isValid()) return 'Choose a date and time.';
  if (!until.isAfter(now)) return 'Choose a time in the future.';
  if (until.valueOf() > now.valueOf() + MAX_SNOOZE_MS) {
    return `Snooze for ${MAX_SNOOZE_DAYS} days at most.`;
  }
  return null;
};

/** "Today 2:49 PM", "Tomorrow 9:00 AM", "Mon 9:00 AM", "Nov 3, 9:00 AM". */
export const calendarTime = (at: Moment, now: Moment): string =>
  at.calendar(now, {
    sameDay: '[Today] h:mm A',
    nextDay: '[Tomorrow] h:mm A',
    lastDay: '[Yesterday] h:mm A',
    nextWeek: 'ddd h:mm A',
    lastWeek: 'ddd h:mm A',
    sameElse:
      at.year() === now.year() ? 'MMM D, h:mm A' : 'MMM D, YYYY, h:mm A',
  });
